import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  AppState, 
  AppAction, 
  KanbanTask, 
  TaskStatus, 
  TemplateType, 
  TaskScope, 
  PriorityTier,
  RecurrenceRangeType 
} from '@/types';
import { useTaskState } from '../features/kanban-engine/hooks/useTaskState';
import {
  Wrench, History, Calendar,
  Loader2, Activity, LayoutDashboard,
  ArrowUpRight, TrendingUp, Plus, ShieldAlert,
  Home, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/Button'; // Fixed import path to lowercase if standard
import { useNavigate } from 'react-router-dom';
import { NLPMaintenanceInput } from '../features/maintenance-engine/components/NLPMaintenanceInput';
import { HistoricalTaskCreator } from '../features/maintenance-engine/components/HistoricalTaskCreator';
import { ParsedMaintenanceIntent } from '../features/maintenance-engine/utils/maintenanceParser';

interface MaintenanceProps {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

const MAINTENANCE_BOARD_ID = 'maintenance-board';

/**
 * 🛠️ UTILITY: Mapping layer for Loose Data to Strict KanbanTask Types
 * This resolves the "createTask" argument errors by ensuring objects are correctly structured.
 */
const mapDataToTaskObjects = (data: any) => {
  const recurrence: KanbanTask['recurrence'] = data.recurrence ? {
    isEnabled: data.recurrence.isEnabled ?? false,
    interval: data.recurrence.interval ?? 0,
    // Use optional chaining for frequency and cast
    frequency: (data.recurrence.frequency as RecurrenceRangeType | null) ?? null,
    completionTrigger: data.recurrence.completionTrigger ?? false,
    lastResetDate: data.recurrence.lastResetDate as string | undefined,
  } : undefined;

  const consumption: KanbanTask['consumption'] = data.consumption ? {
    quantity: data.consumption.quantity ?? 1,
    remainingQuantity: data.consumption.remainingQuantity ?? 1,
    unit: data.consumption.unit ?? 'unit',
    // Use optional chaining for safe access to nested properties
    stockLevel: data.consumption.stockLevel as number | undefined,
    averageCycle: data.consumption.averageCycle as number | undefined,
    lastAddedHistory: data.consumption.lastAddedHistory as string | undefined,
    dayBetweenRecurrence: data.consumption.dayBetweenRecurrence as number[] | undefined,
    cost: data.consumption.cost as number | undefined, // This line is specifically addressed
  } : undefined;

  return { recurrence, consumption };
};

export const Maintenance: React.FC<MaintenanceProps> = ({ state, dispatch }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'dashboard' | 'log' | 'schedule' | 'history'>('dashboard');
  const [showManual, setShowManual] = useState(false);

  const hasActiveHousehold = !!state.currentHouseholdId;

  const maintenanceData = useMemo(() => (state.maintenanceTasks || []).filter(task =>
    task.templateType === TemplateType.MAINTENANCE
  ), [state.maintenanceTasks]);

  const { tasks, createTask } = useTaskState(
    maintenanceData, 
    state.currentUser?.id || 'guest',
    dispatch,
    state.currentHouseholdId,
    state.currentUser?.defaultScope || 'BOTH',
    state.currentUser?.permissions  
  );

  // --- 📊 ANALYTICS ENGINE (REAL-TIME DERIVATIONS) ---
  const analytics = useMemo(() => {
    const historical = tasks.filter((t: KanbanTask) => t.status === 'history');
    
    // 🛡️ Fix: Optional chaining on consumption to prevent crash
    const totalSpend = historical.reduce((acc: number, task: KanbanTask) => {
      const costValue = task.consumption?.cost || 0;
      return acc + costValue;
    }, 0);

    const getHealth = (taskName: string): number => {
      const task = tasks.find((t: KanbanTask) => t.title.toLowerCase().includes(taskName.toLowerCase()));
      if (!task || !task.recurrence?.isEnabled || !task.dueDate) return 100;

      const resetBase = task.recurrence.lastResetDate || task.completedAt || new Date(task.localChangeTimestamp || 0).toISOString();
      if (!resetBase || new Date(resetBase).toString() === 'Invalid Date') return 0;

      const lastReset = new Date(resetBase);
      const now = new Date();
      let expectedNextDueDate = new Date(lastReset);

      const interval = task.recurrence.interval || 1;
      const frequency = task.recurrence.frequency;

      if (frequency === 'years') expectedNextDueDate.setFullYear(expectedNextDueDate.getFullYear() + interval);
      else if (frequency === 'months') expectedNextDueDate.setMonth(expectedNextDueDate.getMonth() + interval);
      else if (frequency === 'weeks') expectedNextDueDate.setDate(expectedNextDueDate.getDate() + (interval * 7));
      else if (frequency === 'days') expectedNextDueDate.setDate(expectedNextDueDate.getDate() + interval);
      else return 100;

      const totalCycleDuration = expectedNextDueDate.getTime() - lastReset.getTime();
      const elapsedCycleDuration = now.getTime() - lastReset.getTime();

      if (totalCycleDuration <= 0) return 0;

      const healthPercentage = 100 - Math.min(100, Math.max(0, (elapsedCycleDuration / totalCycleDuration) * 100));
      return Math.round(healthPercentage);
    };

    return {
      totalSpend,
      historyCount: historical.length,
      activeCount: tasks.filter((t: KanbanTask) => t.status !== 'history' && t.status !== 'finalized').length,
      assetHealth: {
        hvac: getHealth('hvac'),
        vehicle: getHealth('oil') || getHealth('vehicle'),
        appliances: getHealth('filter') || getHealth('dishwasher')
      }
    };
  }, [tasks]);

  // 1. Loading State
  if (state.isBooting && state.tasks.length === 0) {
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-4">
        <Loader2 className="animate-spin text-indigo-500 w-8 h-8" />
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Loading Ledger...</p>
      </div>
    );
  }

  // 2. 🛡️ Permission Access Denied State
  if (!hasActiveHousehold) {
    return (
      <div className="max-w-xl mx-auto py-32 px-6 text-center space-y-8 animate-in fade-in zoom-in-95 duration-500">
        <div className="w-24 h-24 bg-rose-50 dark:bg-rose-900/20 rounded-[2.5rem] flex items-center justify-center mx-auto text-rose-500 shadow-xl shadow-rose-500/10">
          <ShieldAlert className="w-12 h-12" />
        </div>
        <div className="space-y-4">
          <h2 className="text-3xl font-black text-slate-900 dark:text-white uppercase italic tracking-tighter">
            {t('maint.access_denied', 'Shared Protocol Required')}
          </h2>
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400 leading-relaxed">
            Maintenance tracking is a <strong>Household-Only</strong> utility. Personal partitions do not support asset ledgers to maintain data integrity across shared living spaces.
          </p>
        </div>
        <div className="flex flex-col gap-3">
          <Button
            onClick={() => navigate('/settings')}
            className="w-full h-14 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-black uppercase tracking-widest text-xs"
          >
            <Home className="w-4 h-4 mr-2" /> {t('maint.setup_household', 'Initialize Household')} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="w-full h-14 rounded-2xl border-2 border-slate-100 dark:border-slate-800 font-black uppercase tracking-widest text-xs"
          >
            {t('common.return_home', 'Return to Terminal')}
          </Button>
        </div>
      </div>
    );
  }

  // 3. Main Interface
  return (
    <div className="max-w-6xl mx-auto space-y-8 p-6 pb-32 animate-in fade-in duration-500">
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tighter uppercase italic leading-none">
            {t('nav.maintenance', 'Asset Ledger')}
          </h1>
          <p className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.3em] mt-2">
            {t('maint.subtitle', 'Household Health Registry')}
          </p>
        </div>

        <div className="flex bg-slate-100 dark:bg-slate-800 p-1.5 rounded-[1.5rem] shadow-inner w-full md:w-auto">
          {[
            { id: 'dashboard', label: 'Stats', icon: LayoutDashboard },
            { id: 'log', label: 'Actions', icon: Wrench },
            { id: 'schedule', label: 'Rhythms', icon: Calendar },
            { id: 'history', label: 'Ledger', icon: History }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex-1 md:flex-none px-5 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-2 ${activeTab === tab.id ? 'bg-white dark:bg-slate-700 shadow-md text-indigo-600' : 'text-slate-400'}`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>
      </header>

      {/* --- DASHBOARD TAB --- */}
      {activeTab === 'dashboard' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 bg-white dark:bg-slate-900 p-8 rounded-[3rem] border border-slate-100 dark:border-slate-800 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 right-0 p-8 opacity-10">
              <TrendingUp className="w-32 h-32 text-indigo-500" />
            </div>
            <h3 className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-8">{t('maint.tco', 'Total Cost of Ownership')}</h3>
            <div className="flex items-end gap-2">
              <span className="text-6xl font-black text-slate-900 dark:text-white leading-none">${analytics.totalSpend.toLocaleString()}</span>
              <span className="text-indigo-500 font-bold mb-2 flex items-center gap-1 text-xs">
                <ArrowUpRight className="w-4 h-4" /> 12% vs LY
              </span>
            </div>

            <div className="grid grid-cols-3 gap-4 mt-12">
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-300 uppercase">Records</p>
                <p className="text-xl font-bold">{analytics.historyCount}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-300 uppercase">Active</p>
                <p className="text-xl font-bold">{analytics.activeCount}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-900 p-8 rounded-[3rem] text-white">
            <h3 className="text-[10px] font-black uppercase opacity-50 tracking-widest mb-6">{t('maint.health', 'Asset Health')}</h3>
            <div className="space-y-6">
              {Object.entries(analytics.assetHealth).map(([key, val]) => (
                <div key={key} className="space-y-2">
                  <div className="flex justify-between text-[10px] font-black uppercase">
                    <span>{key}</span>
                    <span className={val < 30 ? 'text-rose-500' : 'text-indigo-400'}>{val}%</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-1000 ${val < 30 ? 'bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.5)]' : 'bg-indigo-500'}`}
                      style={{ width: `${val}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* --- LOG TAB --- */}
      {activeTab === 'log' && (
        <div className="space-y-8">
          <NLPMaintenanceInput onConfirm={(data: ParsedMaintenanceIntent) => {
            const { recurrence, consumption } = mapDataToTaskObjects(data);
            createTask(
              data.title,
              TemplateType.MAINTENANCE,
              MAINTENANCE_BOARD_ID,
              data.tags || ['maintenance'],
              (data.scope || 'household') as TaskScope,
              recurrence,
              data.dueDate as string | undefined,
              data.description as string | undefined,
              data.priorityTier as PriorityTier,
              data.cost as number | undefined,
              consumption
            );
          }} />
          <Button variant="outline" onClick={() => setShowManual(true)} className="w-full h-16 rounded-[1.5rem] bg-white dark:bg-slate-900 text-indigo-600 border-2 border-slate-100 dark:border-slate-800 shadow-sm font-black uppercase tracking-widest">
            <Plus className="w-5 h-5 mr-2" /> {t('maint.manual_entry', 'Detailed Manual Entry')}
          </Button>
        </div>
      )}

      {/* --- LEDGER TAB --- */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          {tasks.filter((t: KanbanTask) => t.status === 'history').map((log: KanbanTask) => (
            <div key={log.id} className="bg-white dark:bg-slate-900 p-6 rounded-[2rem] border border-slate-100 dark:border-slate-800 shadow-sm flex items-center justify-between group hover:border-indigo-200 transition-all">
              <div className="flex items-center gap-6">
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl text-slate-400 group-hover:text-indigo-500 transition-colors">
                  <History className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-black text-lg text-slate-800 dark:text-slate-100 uppercase italic tracking-tighter">{log.title}</h4>
                  <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">
                    {log.completedAt ? new Date(log.completedAt).toLocaleDateString() : 'Historical'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-black text-slate-900 dark:text-white leading-none">
                  ${(log.consumption?.cost || 0).toLocaleString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Manual Entry Drawer */}
      {showManual && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setShowManual(false)} />
          <HistoricalTaskCreator
            onClose={() => setShowManual(false)}
            onAdd={(data: any) => {
              const { recurrence, consumption } = mapDataToTaskObjects(data);
              createTask(
                data.title,
                TemplateType.MAINTENANCE,
                MAINTENANCE_BOARD_ID,
                data.tags || ['maintenance'],
                (data.scope || 'household') as TaskScope,
                recurrence,
                data.dueDate as string | undefined,
                data.description as string | undefined,
                data.priorityTier as PriorityTier,
                data.cost as number | undefined,
                consumption
              );
              setShowManual(false);
            }}
          />
        </div>
      )}
    </div>
  );
};